import {
  GetCurrentEncounterStatusResponse,
  WorkflowResponse,
  DashboardStatus,
  EncounterStatusIdsType,
} from '@/api/api.schemas';
import { Button } from '@/components/button';
import { TranslationFunction } from '@/contexts/i18n-context';
import { TranslationKey } from '@/contexts/translations';
import { CONDITION_PAGE_STATUS_INCREMENT } from '@/util/constants';
import styles from './dashboard-page.module.css';

interface GetVisitPathProps {
  encounter: GetCurrentEncounterStatusResponse;
  encounterWorkflow?: WorkflowResponse;
}

function getVisitPath({ encounter, encounterWorkflow }: GetVisitPathProps) {
  if (!encounter.dashboardStatus || !encounter.encounterStatusId) {
    return undefined;
  }

  const conditionalPageMappings = encounterWorkflow?.pages.reduce(
    (acc, page, index) => {
      const statusId =
        EncounterStatusIdsType.ConditionInformationPage1 + index * CONDITION_PAGE_STATUS_INCREMENT;
      acc[statusId] = `/visit/${encounter.encounterId}/questions/${page.pageId}`;
      return acc;
    },
    {} as Record<number, string>,
  );

  const visitPathConfig: Record<number, string> = {
    [EncounterStatusIdsType.InsuranceInformation]: `/visit/${encounter.encounterId}/insurance-information`,
    ...conditionalPageMappings,
    [EncounterStatusIdsType.ProviderSelection]: `/visit/${encounter.encounterId}/choose-provider`,
    [EncounterStatusIdsType.PharmacySelection]: `/visit/${encounter.encounterId}/choose-pharmacy`,
    [EncounterStatusIdsType.ClinicalInformationMedicalIntake]: `/visit/${encounter.encounterId}/health-and-lifestyle`,
    [EncounterStatusIdsType.ClinicalInformationMedicalHistory]: `/visit/${encounter.encounterId}/medical-history`,
    [EncounterStatusIdsType.PreSubmitReview]: `/visit/${encounter.encounterId}/review`,
    [EncounterStatusIdsType.PreSubmitIdentityVerification]: `/visit/${encounter.encounterId}/identity-verification`,
    [EncounterStatusIdsType.PreSubmitPaymentInfo]: `/visit/${encounter.encounterId}/payment`,
    [EncounterStatusIdsType.InWaitingRoom]: `/visit/${encounter.encounterId}/review`,
  };

  return visitPathConfig[encounter.encounterStatusId];
}

export type ButtonConfig =
  | {
      text: TranslationKey;
      to?: string;
      as: 'a';
      onClick?: never;
    }
  | {
      text: TranslationKey;
      onClick?: () => void;
      to?: never;
      as?: 'button';
      loading?: boolean;
    }
  | {
      as: 'logout';
      text?: never;
      to?: never;
      onClick?: never;
    };

type ValidButtonConfig =
  | { to: string; as: 'a'; onClick?: never }
  | { onClick: () => void; to?: never; as?: 'button' }
  | { as: 'logout' };

export function isButtonConfigValid(
  config: Omit<ButtonConfig, 'text'>,
): config is ValidButtonConfig {
  if (config.as === 'logout') {
    return true;
  }

  if (config.as === 'a') {
    return !!config.to;
  }
  return !!config.onClick;
}

interface TranslationComponentProps {
  t: TranslationFunction;
}

type TranslationComponent = (props: TranslationComponentProps) => React.ReactNode;

interface StatusConfig {
  Title: TranslationComponent;
  Description: TranslationComponent;
  primaryButton?: ButtonConfig;
  secondaryButton?: ButtonConfig;
}

interface EncounterStatusConfig {
  currentEncounter: GetCurrentEncounterStatusResponse;
  onCancelEncounter: () => void;
  onResubmitEncounter: () => void;
  isResubmittingEncounter: boolean;
  encounterWorkflow?: WorkflowResponse;
}

export function getDashboardConfig(config: EncounterStatusConfig): StatusConfig {
  const buttonsConfig = getButtonsConfig(config);

  const { currentEncounter } = config;

  return {
    Title: ({ t }) => t(`dashboard.dashboardStatus.${currentEncounter.dashboardStatus}.title`),
    Description: (props) => <DescriptionComponent {...props} currentEncounter={currentEncounter} />,
    primaryButton: buttonsConfig.primaryButton,
    secondaryButton: buttonsConfig.secondaryButton,
  };
}

function DescriptionComponent({
  t,
  currentEncounter,
}: TranslationComponentProps & { currentEncounter: GetCurrentEncounterStatusResponse }) {
  if (currentEncounter.dashboardStatus === DashboardStatus.VisitPausedForEdits) {
    return t('dashboard.dashboardStatus.VisitPausedForEdits.description', {
      link: (
        <Button
          key="cancel-paused-visit-link"
          as="a"
          to={`/visit/${currentEncounter.encounterId}/cancel`}
          variant="link"
          color="accent"
          className={styles.cancelItNowLink}
        >
          {t('dashboard.dashboardStatus.VisitPausedForEdits.cancelItNow')}
        </Button>
      ),
    });
  }

  if (currentEncounter.dashboardStatus === DashboardStatus.LocationNotSupported) {
    return t('dashboard.dashboardStatus.LocationNotSupported.description', {
      contactSupportLink: (
        <Button
          key="contact-support-link"
          as="a"
          to="/support"
          variant="link"
          color="accent"
          className={styles.cancelItNowLink}
        >
          {t('dashboard.dashboardStatus.LocationNotSupported.contactSupport')}
        </Button>
      ),
    });
  }

  if (currentEncounter.dashboardStatus === DashboardStatus.MedicaidNotSupported) {
    return t('dashboard.dashboardStatus.MedicaidNotSupported.description', {
      state:
        currentEncounter.stateName ?? t('dashboard.dashboardStatus.MedicaidNotSupported.yourState'),
    });
  }

  return t(`dashboard.dashboardStatus.${currentEncounter.dashboardStatus}.description`);
}

interface ButtonsConfig {
  primaryButton?: ButtonConfig;
  secondaryButton?: ButtonConfig;
}

const startVisitButton: ButtonConfig = {
  text: 'dashboard.buttons.startVisit',
  to: '/visit/policies-consent',
  as: 'a',
};

function treatmentPlanButtonGenerator(text: TranslationKey) {
  return (encounterId?: number | null): ButtonConfig => {
    const to = encounterId ? `/visit/${encounterId}/tx-plan/treatment` : undefined;

    return { text, as: 'a', to };
  };
}

const viewCompletedTreatmentPlanButton = treatmentPlanButtonGenerator(
  'dashboard.buttons.viewMyTreatmentPlan',
);
const viewTriagedOutTreatmentPlanButton = treatmentPlanButtonGenerator(
  'dashboard.buttons.viewTriagedOutVisit',
);
const viewCanceledTreatmentPlanButton = treatmentPlanButtonGenerator(
  'dashboard.buttons.viewCanceledVisit',
);
const viewDiagnosisInProgressTreatmentPlanButton = treatmentPlanButtonGenerator(
  'dashboard.buttons.viewMyVisit',
);

function getButtonsConfig({
  currentEncounter,
  onCancelEncounter,
  onResubmitEncounter,
  isResubmittingEncounter,
  encounterWorkflow,
}: EncounterStatusConfig): ButtonsConfig {
  switch (currentEncounter.dashboardStatus) {
    case DashboardStatus.VisitPausedForEdits:
      return {
        primaryButton: {
          text: 'dashboard.buttons.resubmitVisit',
          onClick: onResubmitEncounter,
          loading: isResubmittingEncounter,
        },
        secondaryButton: {
          text: 'dashboard.buttons.continueEditing',
          to:
            getVisitPath({ encounter: currentEncounter, encounterWorkflow }) ??
            `/visit/${currentEncounter.encounterId}/review`,
          as: 'a',
        },
      };
    case DashboardStatus.VisitInProgress:
    case DashboardStatus.IdentityManualReviewApproved:
    case DashboardStatus.IdentityManualReviewRejected:
      return {
        primaryButton: {
          text: 'dashboard.buttons.continueVisit',
          to: getVisitPath({ encounter: currentEncounter, encounterWorkflow }),
          as: 'a',
        },
        secondaryButton: {
          text: 'dashboard.buttons.cancelVisit',
          to: `/visit/${currentEncounter.encounterId}/cancel`,
          as: 'a',
        },
      };
    case DashboardStatus.TreatmentPlanAvailable:
      return {
        primaryButton: viewCompletedTreatmentPlanButton(currentEncounter.encounterId),
      };
    case DashboardStatus.ProfileIncomplete:
      return {
        primaryButton: {
          text: 'dashboard.buttons.completeYourProfile',
          to: '/profile/type-selection',
          as: 'a',
        },
      };
    case DashboardStatus.UnderAgeNoSurrogate:
      return {
        primaryButton: {
          text: 'dashboard.buttons.updateYourProfile',
          to: '/profile/surrogate',
          as: 'a',
        },
      };
    case DashboardStatus.ProfileCompleteNoPrevVisit:
    case DashboardStatus.NotQualifiedForCare:
    case DashboardStatus.LastCompletedVisitOutsideActivePeriod:
    case DashboardStatus.Unknown:
      return {
        primaryButton: startVisitButton,
      };
    case DashboardStatus.SelectNewProvider:
      return {
        primaryButton: {
          text: 'dashboard.buttons.selectDifferentProvider',
          to: `/visit/${currentEncounter.encounterId}/choose-provider?editMode=provider`,
          as: 'a',
        },
        secondaryButton: {
          text: 'dashboard.buttons.requestRefund',
          onClick: onCancelEncounter,
        },
      };
    case DashboardStatus.PatientCanceled:
      return {
        primaryButton: startVisitButton,
        secondaryButton: viewCanceledTreatmentPlanButton(currentEncounter.encounterId),
      };
    case DashboardStatus.AwaitingTreatmentLegacyVisit:
    case DashboardStatus.AwaitingTreatmentVideoRequiredLegacyVisit:
      return {
        primaryButton: viewDiagnosisInProgressTreatmentPlanButton(currentEncounter.encounterId),
      };
    case DashboardStatus.AwaitingTreatmentSelectedProvider:
    case DashboardStatus.AwaitingTreatmentGeneralWaitingRoom:
    case DashboardStatus.AwaitingTreatmentVideoRequired:
      return {
        primaryButton: {
          text: 'dashboard.buttons.editVisit',
          to: getVisitPath({ encounter: currentEncounter, encounterWorkflow }),
          as: 'a',
        },
        secondaryButton: {
          text: 'dashboard.buttons.cancelVisit',
          to: `/visit/${currentEncounter.encounterId}/cancel`,
          as: 'a',
        },
      };
    case DashboardStatus.NewPhotosNeeded:
      return {
        primaryButton: {
          text: 'dashboard.buttons.provideNewPhotos',
          to: `/visit/${currentEncounter.encounterId}/update-photo`,
          as: 'a',
        },
      };
    case DashboardStatus.CanceledDidNotSelectNewProvider:
    case DashboardStatus.CanceledDidNotResubmitAfterEdits:
    case DashboardStatus.CanceledDidNotUploadNewPhotos:
    case DashboardStatus.PatientCanceledDidNotSubmit:
    case DashboardStatus.IncompleteVisitCancelled:
    case DashboardStatus.VisitCancelledByPatient:
      return {
        primaryButton: startVisitButton,
        secondaryButton: viewCanceledTreatmentPlanButton(currentEncounter.encounterId),
      };
    case DashboardStatus.LastCompletedVisitWithinActivePeriod:
      return {
        primaryButton: startVisitButton,
        secondaryButton: viewCompletedTreatmentPlanButton(currentEncounter.encounterId),
      };
    case DashboardStatus.DiagnosisInProgress:
      return {
        primaryButton: viewDiagnosisInProgressTreatmentPlanButton(currentEncounter.encounterId),
      };
    case DashboardStatus.TriagedOutByProvider:
      return {
        primaryButton: viewTriagedOutTreatmentPlanButton(currentEncounter.encounterId),
        secondaryButton: startVisitButton,
      };
    case DashboardStatus.TriagedOutByProviderUnreadTreatmentPlan:
      return {
        primaryButton: viewTriagedOutTreatmentPlanButton(currentEncounter.encounterId),
      };
    case DashboardStatus.VisitInProgressVideoMessaging:
      return {
        primaryButton: {
          text: 'dashboard.buttons.replyToMessage',
          to: `/visit/${currentEncounter.encounterId}/tx-plan/messages`,
          as: 'a',
        },
      };
    case DashboardStatus.MedicaidNotSupported:
    case DashboardStatus.MedicaidStateMismatch:
      return {
        primaryButton: {
          as: 'logout',
        },
      };
    case DashboardStatus.LocationNotSupported:
    case DashboardStatus.IdentityManualReviewPending:
      return {};
  }
}
